import { BaseService } from '../../../../../core/services/base.service';
import { getApiBaseUrl } from '../../../../../core/config/runtimeConfig';
import {
  ConfirmResponse,
  Interview,
  ProposeInterviewPayload,
  PublicProposal,
} from '../models/interview.model';

export class InterviewService {
  static listForApplication(jobApplicationId: string): Promise<Interview[]> {
    return BaseService.Get(`/job-applications/${jobApplicationId}/interviews`);
  }

  static propose(
    jobApplicationId: string,
    payload: ProposeInterviewPayload
  ): Promise<Interview> {
    return BaseService.Post(
      `/job-applications/${jobApplicationId}/interviews`,
      payload
    );
  }

  static reschedule(
    interviewId: string,
    payload: ProposeInterviewPayload
  ): Promise<Interview> {
    return BaseService.Post(`/interviews/${interviewId}/reschedule`, payload);
  }

  static cancel(interviewId: string): Promise<Interview> {
    return BaseService.Post(`/interviews/${interviewId}/cancel`, {});
  }

  static markResult(
    interviewId: string,
    status: 'COMPLETED' | 'NO_SHOW',
    notes?: string
  ): Promise<Interview> {
    return BaseService.Post(`/interviews/${interviewId}/result`, {
      status,
      notes,
    });
  }

  static async agenda(
    fromIso: string,
    toIso: string,
    CallbackCatch?: (error: Error) => void
  ): Promise<Interview[]> {
    const segment = `/interviews/agenda?from=${encodeURIComponent(
      fromIso
    )}&to=${encodeURIComponent(toIso)}`;
    const result = CallbackCatch
      ? await BaseService.Get(segment, CallbackCatch, false)
      : await BaseService.Get(segment);

    return Array.isArray(result) ? result : [];
  }

  static async getPublicProposal(token: string): Promise<PublicProposal> {
    const response = await fetch(
      `${getApiBaseUrl()}/public/interviews/${token}`,
      {
        headers: { accept: 'application/json' },
      }
    );
    if (response.status === 410) {
      throw new Error('GONE');
    }
    if (!response.ok) {
      throw new Error('Failed to load interview proposal');
    }
    return response.json();
  }

  static async confirmPublic(
    token: string,
    slotId: string,
    candidateTimezone?: string
  ): Promise<ConfirmResponse> {
    const response = await fetch(
      `${getApiBaseUrl()}/public/interviews/${token}/confirm`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ slotId, candidateTimezone }),
      }
    );
    if (response.status === 410) {
      throw new Error('GONE');
    }
    if (!response.ok) {
      throw new Error('Failed to confirm interview');
    }
    return response.json();
  }

  static publicIcsUrl(token: string): string {
    return `${getApiBaseUrl()}/public/interviews/${token}/ics`;
  }
}
